import type { BalanceSheetRow } from "../../../../../../api/types/svTypes.ts";
import { BalanceSheetAccountType } from "../../../../../../api/types/svTypes.ts";

export interface BalanceSheetTreeNode {
  id: string;
  row: BalanceSheetRow;
  depth: number;
  children: BalanceSheetTreeNode[];
}

export interface BalanceSheetVisibleRow {
  node: BalanceSheetTreeNode;
  hasChildren: boolean;
  isExpanded: boolean;
}

export type BalanceSheetRowVariant = "header" | "group" | "total" | "item";
export type BalanceSheetExpandMode = "collapsed" | "partial" | "expanded";

export function getBalanceSheetRowNodeId(row: BalanceSheetRow): string {
  const accountNo = row.header.accountNo?.trim();
  if (accountNo) {
    return `${accountNo}|${row.header.accountType}|${row.header.indentationLevel}`;
  }

  return String(row.id);
}

export function normalizeBalanceSheetAccountType(
  accountType: BalanceSheetRow["header"]["accountType"]
): BalanceSheetAccountType {
  if (typeof accountType === "number") {
    return (
      [
        BalanceSheetAccountType.GroupStart,
        BalanceSheetAccountType.Item,
        BalanceSheetAccountType.GroupTotal,
        BalanceSheetAccountType.Header,
      ][accountType] ?? BalanceSheetAccountType.Item
    );
  }

  return accountType;
}

export function getBalanceSheetRowVariant(
  accountType: BalanceSheetRow["header"]["accountType"]
): BalanceSheetRowVariant {
  const normalizedType = normalizeBalanceSheetAccountType(accountType);

  switch (normalizedType) {
    case BalanceSheetAccountType.Header:
      return "header";
    case BalanceSheetAccountType.GroupStart:
      return "group";
    case BalanceSheetAccountType.GroupTotal:
      return "total";
    default:
      return "item";
  }
}

export function buildBalanceSheetTree(rows: BalanceSheetRow[]): BalanceSheetTreeNode[] {
  const roots: BalanceSheetTreeNode[] = [];
  const stack: BalanceSheetTreeNode[] = [];

  for (const row of rows) {
    const depth = Math.max(0, row.header.indentationLevel);
    const node: BalanceSheetTreeNode = {
      id: getBalanceSheetRowNodeId(row),
      row,
      depth,
      children: [],
    };

    while (stack.length > 0) {
      const lastNode = stack[stack.length - 1];
      if (!lastNode || depth > lastNode.depth) {
        break;
      }
      stack.pop();
    }

    if (stack.length === 0) {
      roots.push(node);
    } else {
      const parentNode = stack[stack.length - 1];
      if (parentNode) {
        parentNode.children.push(node);
      }
    }

    stack.push(node);
  }

  return roots;
}

export function getInitialExpandedRowIds(tree: BalanceSheetTreeNode[]): Set<string> {
  const expandedIds = new Set<string>();

  const visit = (node: BalanceSheetTreeNode) => {
    if (node.children.length > 0) {
      expandedIds.add(node.id);
      node.children.forEach(visit);
    }
  };

  tree.forEach(visit);

  return expandedIds;
}

export function getCollapsedRowIds(): Set<string> {
  return new Set<string>();
}

export function getPartiallyExpandedRowIds(tree: BalanceSheetTreeNode[]): Set<string> {
  const expandedIds = new Set<string>();

  const visit = (node: BalanceSheetTreeNode): boolean => {
    if (node.children.length === 0) {
      return false;
    }

    let hasCollapsibleDescendant = false;
    for (const child of node.children) {
      hasCollapsibleDescendant = visit(child) || hasCollapsibleDescendant;
    }

    if (hasCollapsibleDescendant) {
      expandedIds.add(node.id);
    }

    return true;
  };

  tree.forEach(visit);

  return expandedIds;
}

export function getExpandedRowIdsByMode(tree: BalanceSheetTreeNode[], mode: BalanceSheetExpandMode): Set<string> {
  switch (mode) {
    case "collapsed":
      return getCollapsedRowIds();
    case "partial":
      return getPartiallyExpandedRowIds(tree);
    default:
      return getInitialExpandedRowIds(tree);
  }
}

export function resolveExpandModeFromState(
  tree: BalanceSheetTreeNode[],
  expandedRowIds: Set<string>
): BalanceSheetExpandMode | null {
  const collapsed = getExpandedRowIdsByMode(tree, "collapsed");
  const partial = getExpandedRowIdsByMode(tree, "partial");
  const expanded = getExpandedRowIdsByMode(tree, "expanded");

  if (areSetsEqual(expandedRowIds, collapsed)) {
    return "collapsed";
  }

  if (areSetsEqual(expandedRowIds, partial)) {
    return "partial";
  }

  if (areSetsEqual(expandedRowIds, expanded)) {
    return "expanded";
  }

  return null;
}

function areSetsEqual(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) {
    return false;
  }

  for (const item of left) {
    if (!right.has(item)) {
      return false;
    }
  }

  return true;
}

export function getVisibleBalanceSheetRows(
  tree: BalanceSheetTreeNode[],
  expandedRowIds: Set<string>
): BalanceSheetVisibleRow[] {
  const rows: BalanceSheetVisibleRow[] = [];

  const visit = (node: BalanceSheetTreeNode) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = hasChildren && expandedRowIds.has(node.id);

    rows.push({ node, hasChildren, isExpanded });

    if (isExpanded) {
      node.children.forEach(visit);
    }
  };

  tree.forEach(visit);

  return rows;
}
